import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Divider,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Description,
  CloudUpload,
  Download,
  Delete,
  Edit,
  Visibility,
  Folder,
  Storage,
  Category,
  Add,
  Close,
} from '@mui/icons-material';
import { clienteDocumentoService } from '../../services/clienteDocumentoService';
import { ClienteDocumento, ClienteDocumentoFormData, ClienteDocumentoStats } from '../../types';
import { tableHeadRowSx, themedTableContainerSx } from '../../styles/theme';

interface ClientDocumentsProps {
  clientId: number;
}

const ClientDocuments: React.FC<ClientDocumentsProps> = ({ clientId }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [documentos, setDocumentos] = useState<ClienteDocumento[]>([]);
  const [estatisticas, setEstatisticas] = useState<ClienteDocumentoStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Estados para modais
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<ClienteDocumento | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Estados para formulário de upload
  const [uploadForm, setUploadForm] = useState<ClienteDocumentoFormData>({
    descricao: '',
    categoria: 'outros',
    arquivo: null as any,
  });
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Carregar documentos e estatísticas
  const carregarDados = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [documentosResponse, estatisticasResponse] = await Promise.all([
        clienteDocumentoService.listarPorCliente(clientId),
        clienteDocumentoService.estatisticas(clientId),
      ]);

      if (documentosResponse.success) {
        setDocumentos(documentosResponse.data || []);
      } else {
        setError(documentosResponse.message || 'Erro ao carregar documentos');
      }

      if (estatisticasResponse.success) {
        setEstatisticas(estatisticasResponse.data || null);
      }
    } catch (err) {
      setError('Erro ao carregar documentos');
      console.error('Erro ao carregar documentos:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, [clientId]);

  // Upload de documento
  const handleUpload = async () => {
    if (!uploadForm.arquivo) {
      setUploadError('Selecione um arquivo');
      return;
    }

    const validacao = clienteDocumentoService.validarArquivo(uploadForm.arquivo);
    if (!validacao.valido) {
      setUploadError(validacao.erro || 'Arquivo inválido');
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);

      const formData = clienteDocumentoService.criarFormData(uploadForm);
      const response = await clienteDocumentoService.upload(clientId, formData);

      if (response.success) {
        setSuccess('Documento enviado com sucesso');
        setUploadModalOpen(false);
        setUploadForm({ descricao: '', categoria: 'outros', arquivo: null as any });
        setUploadError(null);
        // Recarregar dados após upload bem-sucedido
        setTimeout(() => {
          carregarDados();
        }, 100);
      } else {
        setUploadError(response.message || 'Erro ao enviar documento');
      }
    } catch (err) {
      setUploadError('Erro ao enviar documento');
      console.error('Erro no upload:', err);
    } finally {
      setUploading(false);
    }
  };

  // Download de documento
  const handleDownload = async (documento: ClienteDocumento) => {
    try {
      const blob = await clienteDocumentoService.download(documento.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = documento.nome;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Erro ao fazer download do documento');
      console.error('Erro no download:', err);
    }
  };

  // Visualizar documento
  const handlePreview = async (documento: ClienteDocumento) => {
    try {
      setLoadingPreview(true);
      setSelectedDocument(documento);
      setViewModalOpen(true);
      
      const blob = await clienteDocumentoService.download(documento.id);
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (err) {
      setError('Erro ao abrir visualização do documento');
      console.error('Erro no preview:', err);
      setViewModalOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Fechar modal de visualização e limpar URL
  const handleClosePreview = () => {
    setViewModalOpen(false);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setSelectedDocument(null);
  };

  // Excluir documento
  const handleDelete = async () => {
    if (!selectedDocument) return;

    try {
      const response = await clienteDocumentoService.excluir(selectedDocument.id);
      if (response.success) {
        setSuccess('Documento excluído com sucesso');
        setDeleteModalOpen(false);
        setSelectedDocument(null);
        carregarDados();
      } else {
        setError(response.message || 'Erro ao excluir documento');
      }
    } catch (err) {
      setError('Erro ao excluir documento');
      console.error('Erro na exclusão:', err);
    }
  };

  // Atualizar documento
  const handleUpdate = async () => {
    if (!selectedDocument) return;

    try {
      const response = await clienteDocumentoService.atualizar(selectedDocument.id, {
        descricao: selectedDocument.descricao,
        categoria: selectedDocument.categoria,
      });

      if (response.success) {
        setSuccess('Documento atualizado com sucesso');
        setEditModalOpen(false);
        setSelectedDocument(null);
        carregarDados();
      } else {
        setError(response.message || 'Erro ao atualizar documento');
      }
    } catch (err) {
      setError('Erro ao atualizar documento');
      console.error('Erro na atualização:', err);
    }
  };

  // Limpar mensagens
  const limparMensagens = () => {
    setError(null);
    setSuccess(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight="bold">
          Documentos do Cliente
        </Typography>
        <Button
          variant="contained"
          startIcon={<CloudUpload />}
          onClick={() => setUploadModalOpen(true)}
        >
          Upload Documento
        </Button>
      </Box>

      {/* Alertas */}
      {error && (
        <Alert severity="error" onClose={limparMensagens} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={limparMensagens} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Estatísticas */}
      {estatisticas && (
        <Card sx={{ mb: 3, border: `1px solid ${theme.palette.divider}`, ...(isDark && { backgroundImage: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }) }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Estatísticas
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Box textAlign="center">
                  <Folder color="primary" sx={{ fontSize: 40 }} />
                  <Typography variant="h6">{estatisticas.total_documentos}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total de Documentos
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box textAlign="center">
                  <Storage color="primary" sx={{ fontSize: 40 }} />
                  <Typography variant="h6">{estatisticas.total_tamanho_formatado}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Espaço Utilizado
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Por Categoria:
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {estatisticas.documentos_por_categoria.map((item, index) => (
                      <Chip
                        key={index}
                        label={`${clienteDocumentoService.obterLabelCategoria(item.categoria)}: ${item.quantidade}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Tabela de Documentos */}
      <TableContainer component={Paper} elevation={1} sx={themedTableContainerSx(theme)}>
        <Table>
          <TableHead>
            <TableRow sx={tableHeadRowSx(theme)}>
              <TableCell>Nome</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Upload em</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!documentos || documentos.length === 0) ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Description sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography color="text.secondary">
                    Nenhum documento encontrado
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => setUploadModalOpen(true)}
                    sx={{ mt: 2 }}
                  >
                    Adicionar Primeiro Documento
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              documentos.map((documento) => (
                <TableRow key={documento.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Description color="primary" />
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {documento.nome}
                        </Typography>
                        {documento.descricao && (
                          <Typography variant="caption" color="text.secondary">
                            {documento.descricao}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={clienteDocumentoService.obterLabelCategoria(documento.categoria)}
                      size="small"
                      sx={{
                        backgroundColor: clienteDocumentoService.obterCorCategoria(documento.categoria),
                        color: 'white',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {documento.tipo_arquivo}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(documento.createdAt).toLocaleDateString('pt-BR')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={1} justifyContent="center">
                      <Tooltip title="Visualizar">
                        <IconButton
                          size="small"
                          onClick={() => handlePreview(documento)}
                          color="info"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(documento)}
                          color="primary"
                        >
                          <Download />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedDocument(documento);
                            setEditModalOpen(true);
                          }}
                          color="info"
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedDocument(documento);
                            setDeleteModalOpen(true);
                          }}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal de Upload */}
      <Dialog open={uploadModalOpen} onClose={() => setUploadModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Upload de Documento</Typography>
            <IconButton onClick={() => setUploadModalOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.zip,.rar"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setUploadForm({ ...uploadForm, arquivo: file });
                  setUploadError(null);
                }
              }}
              style={{ display: 'none' }}
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                fullWidth
                sx={{ py: 2 }}
              >
                {uploadForm.arquivo ? uploadForm.arquivo.name : 'Selecionar Arquivo'}
              </Button>
            </label>

            <FormControl fullWidth>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={uploadForm.categoria}
                onChange={(e) => setUploadForm({ ...uploadForm, categoria: e.target.value as any })}
                label="Categoria"
              >
                <MenuItem value="contrato">Contrato</MenuItem>
                <MenuItem value="documento_pessoal">Documento Pessoal</MenuItem>
                <MenuItem value="comprovante">Comprovante</MenuItem>
                <MenuItem value="outros">Outros</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Descrição (opcional)"
              value={uploadForm.descricao}
              onChange={(e) => setUploadForm({ ...uploadForm, descricao: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />

            {uploadError && (
              <Alert severity="error">{uploadError}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadModalOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || !uploadForm.arquivo}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
          >
            {uploading ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Edição */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Documento</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Descrição"
              value={selectedDocument?.descricao || ''}
              onChange={(e) => setSelectedDocument(prev => prev ? { ...prev, descricao: e.target.value } : null)}
              multiline
              rows={3}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={selectedDocument?.categoria || 'outros'}
                onChange={(e) => setSelectedDocument(prev => prev ? { ...prev, categoria: e.target.value as any } : null)}
                label="Categoria"
              >
                <MenuItem value="contrato">Contrato</MenuItem>
                <MenuItem value="documento_pessoal">Documento Pessoal</MenuItem>
                <MenuItem value="comprovante">Comprovante</MenuItem>
                <MenuItem value="outros">Outros</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleUpdate} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o documento "{selectedDocument?.nome}"?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Visualização de Documento */}
      <Dialog 
        open={viewModalOpen} 
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Visualizar Documento: {selectedDocument?.nome}</Typography>
            <IconButton onClick={handleClosePreview}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
          {loadingPreview ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <CircularProgress />
            </Box>
          ) : previewUrl ? (
            <Box flex={1} display="flex" flexDirection="column">
              {selectedDocument?.tipo_arquivo.startsWith('image/') ? (
                <Box 
                  display="flex" 
                  justifyContent="center" 
                  alignItems="center" 
                  p={2} 
                  flex={1}
                  sx={{ bgcolor: alpha(theme.palette.background.default, 0.6), overflow: 'auto' }}
                >
                  <img 
                    src={previewUrl} 
                    alt={selectedDocument.nome} 
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
                  />
                </Box>
              ) : selectedDocument?.tipo_arquivo === 'application/pdf' ? (
                <iframe
                  src={`${previewUrl}#toolbar=0`}
                  title={selectedDocument.nome}
                  width="100%"
                  height="100%"
                  style={{ border: 'none', flex: 1 }}
                />
              ) : selectedDocument?.tipo_arquivo === 'text/plain' ? (
                <Box 
                  p={3} 
                  flex={1} 
                  sx={{
                    bgcolor: alpha(theme.palette.background.default, 0.8),
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    color: 'text.primary',
                  }}
                >
                  <iframe
                    src={previewUrl}
                    title={selectedDocument.nome}
                    width="100%"
                    height="100%"
                    style={{ border: 'none', flex: 1 }}
                  />
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flex={1} p={4}>
                  <Description sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Visualização não disponível para este tipo de arquivo
                  </Typography>
                  <Typography color="text.secondary" align="center" mb={3}>
                    O arquivo "{selectedDocument?.nome}" ({selectedDocument?.tipo_arquivo}) não pode ser visualizado diretamente no navegador.
                  </Typography>
                  <Button 
                    variant="contained" 
                    startIcon={<Download />}
                    onClick={() => {
                      if (selectedDocument) handleDownload(selectedDocument);
                    }}
                  >
                    Baixar Arquivo para Ver
                  </Button>
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Fechar</Button>
          {selectedDocument && (
            <Button 
              variant="contained" 
              startIcon={<Download />}
              onClick={() => handleDownload(selectedDocument)}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientDocuments; 